// Volume helpers — shared across the My Position tab and the Stock
// Metrics tab so both surfaces compare today's volume against the
// 3-month trailing average consistently.
//
// Round 95at (Jun 2026).

/**
 * Compute a small "unusual activity" badge if today's volume is
 * meaningfully out of band vs. the trailing 3-month average.
 *
 *   • ≥ 3.0×  → "+3.2× avg"  solid green  ("heavy — positive activity")
 *   • ≥ 2.0×  → "+2.1× avg"  solid green  ("above-average")
 *   • ≤ 0.4×  → "0.3× avg"   solid red    ("light/quiet — negative")
 *   • otherwise → null (no badge — clutter-free)
 *
 * Solid fills with white text read clearly in both light and dark
 * themes — no theme-specific overrides needed.
 *
 * Returns { label, classes, severity } or null.
 */
export function unusualVolumeBadge(current, avg) {
  const c = Number(current);
  const a = Number(avg);
  // Round 95bd (Jun 2026) — require BOTH numbers to be strictly
  // positive. A current volume of 0 means the market hasn't traded
  // the name yet (weekend / pre-open / halted / illiquid), which is
  // "no signal", not "quiet day". Without this gate CORB and similar
  // 0-volume rows surfaced as red "0.0× avg" chips incorrectly.
  if (!Number.isFinite(c) || !Number.isFinite(a) || a <= 0 || c <= 0) return null;
  const ratio = c / a;
  if (ratio >= 3.0) {
    return {
      ratio,
      label: `${ratio.toFixed(1)}× avg`,
      classes: 'bg-emerald-600 text-white border border-emerald-700',
      severity: 'heavy',
    };
  }
  if (ratio >= 2.0) {
    return {
      ratio,
      label: `${ratio.toFixed(1)}× avg`,
      classes: 'bg-emerald-600 text-white border border-emerald-700',
      severity: 'above',
    };
  }
  if (ratio <= 0.4) {
    return {
      ratio,
      label: `${ratio.toFixed(1)}× avg`,
      classes: 'bg-rose-600 text-white border border-rose-700',
      severity: 'light',
    };
  }
  return null;
}
